import { pure } from '@core/helpers/pure';
import { TuiInput, TuiTextfield } from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TuiDataList, TuiError, TuiButton } from '@taiga-ui/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  merge,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import type { Nullable } from '@core/helpers/nullable';
import { TuiSelect } from '@taiga-ui/kit';
import { XAlertService } from '@core/services/x-alert.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { AsyncPipe } from '@angular/common';
import { RequestService } from '@core/services/request/request.service';
import {
  RequestDeviceMountType,
  RequestDeviceOnboardVoltage,
} from '@core/types/RequestDevice';
import type { RequestDevice } from '@core/types/RequestDevice';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { RequestDeviceProtectiveCaseInputComponent } from '../request-device-protective-case-input/request-device-protective-case-input.component';
import { RequestDeviceProtectiveCaseFormFields } from '../request-device-protective-case-input/types';
import type { ProtectiveCaseFormControl } from '../request-device-protective-case-input/types';
import { DeviceDetectorService } from '@core/services';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { WarehouseService } from '@core/services/warehouse/warehouse.service';
import { TranslatePipe } from '@ngx-translate/core';

enum FormFields {
  Sim1 = 'name',
  Sim2 = 'secondSim',
  ProtectiveCaseSubform = 'protectiveCase',
}

@Component({
  selector: 'x-request-form-devices-request-device-update',
  imports: [
    TuiTextfield,
    AsyncPipe,
    ReactiveFormsModule,
    TuiButton,
    RequestDeviceProtectiveCaseInputComponent,
    TuiError,
    TuiInput,
    TranslatePipe,
    TuiDataList,
    TuiSelect,
  ],
  templateUrl: './request-form-devices-request-device-update.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormDevicesRequestDeviceUpdateComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly xAlertService = inject(XAlertService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly destroyRef = inject(DestroyRef);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly $request = this.requestService.$request;
  readonly $device = input.required<RequestDevice>({ alias: 'device' });
  readonly updateRequestDevice$ = new Subject<void>();
  protected readonly $isMobile = this.deviceService.$isMobile;
  // true - данные устройства изменились
  // false - пользователь нажал "Отмена", данные не изменены
  formClosed = output<boolean>();

  readonly FormFields = FormFields;

  readonly formGroup = this.formBuilder.group({
    [FormFields.Sim1]: this.formBuilder.control<Nullable<string>>(null, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
    ]),
    [FormFields.Sim2]: this.formBuilder.control<Nullable<string>>(null, [
      Validators.pattern('^[0-9]*$'),
    ]),
    [FormFields.ProtectiveCaseSubform]: null,
  });

  public readonly mountTypeOptions = Object.values(RequestDeviceMountType);

  public readonly onboardVoltageOptions = Object.values(
    RequestDeviceOnboardVoltage,
  );

  public $isMountTypeFormFieldShown = signal(false);

  public $isOnboardVoltageFormFieldShown = signal(false);

  @pure
  get device() {
    return this.$device();
  }
  @pure
  get sim1Field() {
    return this.formGroup.controls[FormFields.Sim1];
  }

  @pure
  get sim2Field() {
    return this.formGroup.controls[FormFields.Sim2];
  }

  @pure
  get protectiveCaseSubformField(): ProtectiveCaseFormControl {
    return this.formGroup.controls[FormFields.ProtectiveCaseSubform];
  }

  get hasProtectiveCaseValue(): boolean {
    return this.protectiveCaseSubformField.value
      ? this.protectiveCaseSubformField.value[
          RequestDeviceProtectiveCaseFormFields.HasProtectiveCase
        ]
      : false;
  }

  get protectiveCaseNumberValue(): Nullable<number> {
    return this.protectiveCaseSubformField.value
      ? this.protectiveCaseSubformField.value[
          RequestDeviceProtectiveCaseFormFields.ProtectiveCaseNumber
        ]
      : null;
  }

  ngOnInit() {
    this.updateRequestDevice$
      .pipe(
        switchMap(() => this.performRequestDeviceUpdate$()),
        // switchMap(() =>
        //   this.requestDevicesService.checkWarehouseDeviceSoldAttempt$(
        //     this.sim1Field.value!,
        //   ),
        // ),
        switchMap(() =>
          this.warehouseService.unSellDevice$(
            this.$request(),
            this.device.name,
          ),
        ),
        tap(() => {
          this.requestService.reloadRequest();
          this.formGroup.reset();
          this.formClosed.emit(true);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // to show a validation message as soon as a user starts typing
    merge(this.sim1Field.valueChanges, this.sim2Field.valueChanges)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sim1Field.markAsTouched();
        this.sim2Field.markAsTouched();
      });

    this.formGroup.patchValue(
      this.device as Parameters<typeof this.formGroup.patchValue>[0],
    );
  }

  performRequestDeviceUpdate$() {
    if (this.formGroup.valid) {
      return this.requestDevicesService
        .updateRequestDevice$(
          this.device.id,
          {
            name: this.sim1Field.value!,
            secondSim: this.sim2Field.value,
            hasProtectCase: this.hasProtectiveCaseValue,
            protectCaseNumber: this.protectiveCaseNumberValue,
          },
          this.$isMobile(),
        )
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (err.status === HttpStatusCode.BadRequest) {
              this.xAlertService.showMessage(err.error, {
                appearance: 'negative',
                autoClose: 10000,
              });

              return EMPTY;
            }

            return throwError(() => err);
          }),
        );
    }

    return EMPTY;
  }

  cancelUpdateRequestDevice() {
    this.formGroup.reset();
    this.formClosed.emit(false);
  }
}
